import * as React from 'react';
import {useRef, useState} from 'react';
import {useCookies} from 'react-cookie';
import {Button, Icon, Text} from '@flexe/ui-components';
import {Warehouse} from '../shared/services/UserBarService';
import DoWhenClickOutside from '../shared/DoWhenClickOutside';

interface WarehouseSelectorProps {
  isShipper: boolean;
  initiallySelectedWarehouse: Warehouse | null;
  activeWarehouses: Warehouse[];
}

const WarehouseSelector = ({isShipper, initiallySelectedWarehouse, activeWarehouses}: WarehouseSelectorProps) => {
  const [cookies, setCookie, removeCookie] = useCookies(['selected_warehouse_id']);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedWh, setSelectedWh] = useState<Warehouse>(initiallySelectedWarehouse || activeWarehouses[0]);

  const formatWarehouseDisplayName = (wh: Warehouse): string => {
    return `#${wh.id}: ${wh.name}`;
  };

  const onWarehouseClicked = (wh: Warehouse) => {
    setCookie('selected_warehouse_id', wh.id, {path: '/'});
    setSelectedWh(wh);
    triggerNonReactEvent(wh);
  };

  /**
   * Non react pages rely on browser based eventing (formerly done through Jquery).
   * Once all of those pages have been removed, this function can be deleted.
   * @param wh The warehouse that was selected
   */
  const triggerNonReactEvent = (wh: Warehouse) => {
    document.dispatchEvent(
      new CustomEvent('warehouse:selected', {
        detail: {
          id: wh.id
        },
        bubbles: true,
        cancelable: false,
        composed: false
      })
    );
  };

  const warehouseList = (title: string, warehouses: Warehouse[]) => {
    if ((warehouses?.length || 0) < 1) return null;

    return (
      <div>
        <ul>
          <p className="header">{title}</p>
          {warehouses
            .sort((wh1, wh2) => wh1.id - wh2.id)
            .map((wh: Warehouse) => {
              const isSelected = wh.id === selectedWh.id;
              return (
                <li key={wh.id}>
                  <a
                    tabIndex={0}
                    onClick={() => onWarehouseClicked(wh)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onWarehouseClicked(wh);
                      }
                    }}
                    className={isSelected ? 'selected' : 'unselected'}
                  >
                    {isSelected ? <Icon icon="check" /> : null}
                    {formatWarehouseDisplayName(wh)}
                  </a>
                </li>
              );
            })}
        </ul>
      </div>
    );
  };

  if (isShipper || !selectedWh) {
    // Render a placeholder if logged in as a shipper or there are no warehouses to take up space
    return <div className="warehouse-selector" />;
  }

  return (
    <div className="warehouse-selector">
      {(activeWarehouses?.length || 0) < 2 ? (
        <Text level="Body2" bold={true}>
          {formatWarehouseDisplayName(selectedWh)}
        </Text>
      ) : (
        <div>
          <Button onPress={() => setIsOpen(!isOpen)}>
            {formatWarehouseDisplayName(selectedWh)}
            <span className="icon-wrapper" />
            <Icon icon="angle-down" />
          </Button>
          {isOpen && (
            <DoWhenClickOutside callback={() => setIsOpen(false)}>
              <div className="warehouse-options">{warehouseList('Warehouses', activeWarehouses)}</div>
            </DoWhenClickOutside>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehouseSelector;
